import { searchLocationWithTransaction, searchVoucherWithTransaction } from "apis";
import {
  FilterBar,
  FilterDateRange,
  FilterMultiSelect,
  FilterSingleSelect,
  FilterTimeRange,
} from "components/commons";
import { useApi, useMount } from "hooks";
import {
  locationWithTransactionFilterResponse,
  locationWithTransactionFilterRequest,
  voucherWithTransactionFilterRequest,
  voucherWithTransactionFilterResponse,
} from "mappers";
import React, { useCallback, useMemo } from "react";
import { mapObject, mapObjectsToSelect, sortByKeyName } from "services";
import lang from "translations";

const VoucherReportFilter = ({
  filterState,
  requestState,
  modifyFilter,
  clearFilter,
  onClear,
  onApply,
}) => {
  const {
    request: searchLocationRequest,
    loading: loadingLocation,
    mappedData: locations,
  } = useApi({
    api: searchLocationWithTransaction,
    isArray: true,
    mapper: locationWithTransactionFilterResponse,
  });

  const { request: searchVoucherRequest, loading: loadingVoucher, mappedData: vouchers } = useApi({
    api: searchVoucherWithTransaction,
    isArray: true,
    mapper: voucherWithTransactionFilterResponse,
  });

  useMount(() => {
    fetchLocations(requestState);
    fetchVouchers(requestState);
  });

  const fetchLocations = useCallback(
    (requestState) => {
      searchLocationRequest(mapObject({ ...requestState }, locationWithTransactionFilterRequest));
    },
    [searchLocationRequest]
  );

  const fetchVouchers = useCallback(
    (requestState) => {
      searchVoucherRequest(mapObject({ ...requestState }, voucherWithTransactionFilterRequest));
    },
    [searchVoucherRequest]
  );

  const applyFilterCb = useCallback(
    async (searchKey) => {
      await onApply(searchKey);
    },
    [onApply]
  );

  const clearFilterCb = useCallback(() => {
    const { filterState, requestState } = clearFilter();
    onClear(filterState, requestState);
    fetchLocations(requestState);
    fetchVouchers(requestState);
  }, [clearFilter, onClear, fetchLocations, fetchVouchers]);

  const changeDateRangeCb = useCallback(
    (value) => {
      const { requestState } = modifyFilter("dateRange", { value });
      fetchLocations(requestState);
      fetchVouchers(requestState);
    },
    [fetchLocations, fetchVouchers, modifyFilter]
  );

  const locationOptions = useMemo(() => {
    return mapObjectsToSelect(locations, { textKey: "name" });
  }, [locations]);

  const voucherOptions = useMemo(() => {
    return [
      { text: lang.allVouchers, value: null },
      ...mapObjectsToSelect(vouchers, { textKey: "name" }).sort(sortByKeyName("text")),
    ];
  }, [vouchers]);

  return (
    <FilterBar
      className="my-lg"
      filterState={filterState}
      searchBox={false}
      onClear={clearFilterCb}
      onApply={applyFilterCb}
    >
      <FilterDateRange
        id="dateRange"
        label={lang.dateRange}
        maxDays={90}
        value={filterState.dateRange}
        onChange={changeDateRangeCb}
      />
      <FilterTimeRange
        id="timeRange"
        label={lang.timeRange}
        value={filterState.dateRange}
        onChange={(value) => modifyFilter("dateRange", { value })}
      />
      <FilterMultiSelect
        id="locations"
        label={lang.outlet}
        allLabel={lang.allLocations}
        emptyLabel={lang.noLocationAvailable}
        disableWhenEmpty={false}
        loading={loadingLocation}
        options={locationOptions}
        value={filterState.locations}
        onChange={(value) => modifyFilter("locations", { value })}
      />
      <FilterSingleSelect
        id="voucher"
        label={lang.voucher}
        loading={loadingVoucher}
        emptyLabel={lang.noVoucherAvailable}
        summaryFallback={lang.allVouchers}
        options={voucherOptions}
        value={filterState.voucher}
        onChange={(value) => modifyFilter("voucher", { value })}
      />
    </FilterBar>
  );
};

export default VoucherReportFilter;
